import React, { MouseEvent, FC, useRef, useState, useEffect, useMemo } from "react";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Grow from "@mui/material/Grow";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";

import type { ButtonProps } from "@mui/material/Button";

export interface SplitButtonProps extends Omit<ButtonProps, "onChange" | "onClick"> {
  options: any[];
  defaultValue?: any;
  onChange?: (value: string, index: number) => void;
  onClick?: (
    value: string,
    index: number,
    event: MouseEvent<HTMLButtonElement>
  ) => void;
  getOptionLabel?: (option: any) => string;
  getOptionKey?: (option: any) => string | number;
}

const SplitButton: FC<SplitButtonProps> = ({
  options,
  defaultValue,
  onChange,
  onClick,
  getOptionLabel,
  getOptionKey,
  variant = "contained",
  color = "primary",
  disabled = false,
  fullWidth = false,
  ...props
}) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const normalizedOptions = Array.isArray(options) ? options : [];
  // Internal selectedIndex mirrors the controlled value
  const [selectedIndex, setSelectedIndex] = useState<number>(() => {
    if (!normalizedOptions.length) return -1;
    if (defaultValue && normalizedOptions.find((opt) => getOptionKey ? getOptionKey(opt) === defaultValue : opt === defaultValue)) {
      return normalizedOptions.indexOf(defaultValue);
    }
    return 0; // default to first item when value is empty/invalid
  });

  // Keep internal index in sync when value or options change
  useEffect(() => {
    if (!normalizedOptions.length) {
      setSelectedIndex(-1);
      return;
    }
    if (defaultValue && normalizedOptions.find((opt) => getOptionKey ? getOptionKey(opt) === defaultValue : opt === defaultValue)) {
      setSelectedIndex(normalizedOptions.indexOf(defaultValue));
    } else {
      setSelectedIndex(0);
    }
  }, [defaultValue, normalizedOptions]);

  const effectiveValue = selectedIndex >= 0 ? normalizedOptions[selectedIndex] : "";

  const currentLabel = useMemo(() => {
    if (selectedIndex < 0) return "";
    const opt = normalizedOptions[selectedIndex];
    return getOptionLabel ? getOptionLabel(opt) : opt;
  }, [getOptionLabel, normalizedOptions, selectedIndex]);

  const handleMainClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (disabled || selectedIndex < 0) return;
    onClick?.(effectiveValue, selectedIndex, e);
  };

  const handleToggle = () => {
    if (disabled || !normalizedOptions.length) return;
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }
    setOpen(false);
  };

  const handleMenuItemClick = (
    _event: MouseEvent<HTMLLIElement>,
    index: number
  ) => {
    if (disabled) return;
    const newValue = normalizedOptions[index];
    setOpen(false);
    // Update internal state immediately for responsive UI
    setSelectedIndex(index);
    // Delegate selection change to parent (controlled)
    onChange?.(newValue, index);
  };

  return (
    <>
      <ButtonGroup
        ref={anchorRef}
        variant={variant}
        color={color}
        size={props.size}
        disabled={disabled || !normalizedOptions.length}
        fullWidth={fullWidth}
      >
        <Button onClick={handleMainClick} sx={{ flexGrow: fullWidth ? 1 : undefined }} {...props}>
          {currentLabel || ""}
        </Button>
        <Button
          size={props.size}
          aria-controls={open ? "split-button-menu" : undefined}
          aria-expanded={open ? "true" : undefined}
          aria-label="select option"
          aria-haspopup="menu"
          onClick={handleToggle}
          disabled={disabled || !normalizedOptions.length || !!props.loading}
          sx={{ 
            "&.MuiButton-root": { minWidth: "26px"},
            paddingLeft: 0, 
            paddingRight: 0 
          }}
        >
          <ArrowDropDownIcon />
        </Button>
      </ButtonGroup>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        placement="bottom-start"
        style={{ zIndex: 1300 }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom-start" ? "left top" : "left bottom",
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id="split-button-menu" autoFocusItem>
                  {normalizedOptions.map((option, index) => (
                    <MenuItem
                      key={`${option}-${index}`}
                      selected={index === selectedIndex}
                      onClick={(e) => handleMenuItemClick(e as any, index)}
                    >
                      {getOptionLabel ? getOptionLabel(option) : option}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

export default SplitButton;
